import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Database,
  Play,
  Loader2,
  Star,
  Search,
  Sparkles,
  Check,
  X,
  Square,
  RotateCcw,
  Trash2,
  Tag,
  ExternalLink,
  Settings2,
  Globe,
  Users,
  Shield
} from 'lucide-react'
import { motion } from 'motion/react'
import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { sourcesApi, scrapersApi, SearchConfig, SourceProfile, SourceRelationshipType } from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { AddSourceDialog } from '@/components/add-source-dialog'

interface Source {
  id: number
  name: string
  url?: string
  source_type?: string
  scraper_status?: string
  quality_rating?: number | null
  last_scraped_at?: string | null
  companies_scraped?: number
  companies_count?: number
  last_run?: string | null
  industry_tags?: string[]
  search_config?: SearchConfig
  source_profile?: SourceProfile
  source_relationship?: SourceRelationshipType
}

const STATUS_LABELS: Record<string, string> = {
  suggested: 'Suggested',
  analyzing: 'Analyzing',
  ready: 'Ready',
  working: 'Working',
  stopped: 'Stopped',
  error: 'Error',
}

const ACCESS_TYPE_STYLES: Record<string, string> = {
  free: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  freemium: 'bg-sky-100 text-sky-700 border-sky-200',
  paid: 'bg-amber-100 text-amber-700 border-amber-200',
  registration_required: 'bg-slate-100 text-slate-700 border-slate-200',
}

const QUALITY_SCORE: Record<string, number> = {
  verified: 4,
  high: 3,
  medium: 2,
  low: 1,
  unknown: 0,
}

// Source relationship badge styles and labels
const RELATIONSHIP_LABELS: Record<string, string> = {
  competitor_platform: 'Competitor Platform',
  complementary_platform: 'Complementary',
  industry_directory: 'Industry Directory',
  official_registry: 'Official Registry',
  contact_enrichment: 'Contact Enrichment',
  general_directory: 'Directory',
}

const RELATIONSHIP_STYLES: Record<string, string> = {
  competitor_platform: 'bg-rose-100 text-rose-700 border-rose-200',
  complementary_platform: 'bg-purple-100 text-purple-700 border-purple-200',
  industry_directory: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  official_registry: 'bg-blue-100 text-blue-700 border-blue-200',
  contact_enrichment: 'bg-amber-100 text-amber-700 border-amber-200',
  general_directory: 'bg-slate-100 text-slate-700 border-slate-200',
}

// Sort priority for relationship types (lower = higher priority)
const RELATIONSHIP_PRIORITY: Record<string, number> = {
  competitor_platform: 0,
  complementary_platform: 1,
  official_registry: 2,
  industry_directory: 3,
  contact_enrichment: 4,
  general_directory: 5,
}

export default function SourcesPage() {
  const queryClient = useQueryClient()
  const [runningSource, setRunningSource] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [discoveryMessage, setDiscoveryMessage] = useState<string | null>(null)

  const { data, isLoading } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: () => sourcesApi.list({ recheck: true })
  })

  useEffect(() => {
    if (!discoveryMessage) return
    const t = setTimeout(() => setDiscoveryMessage(null), 5000)
    return () => clearTimeout(t)
  }, [discoveryMessage])

  const runScrapeMutation = useMutation({
    mutationFn: (sourceName: string) => scrapersApi.run(sourceName),
    onMutate: (sourceName) => setRunningSource(sourceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
    onSettled: () => setRunningSource(null)
  })

  const updateSourceMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: number; quality_rating?: number; scraper_status?: string }) =>
      sourcesApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sources'] })
  })

  const discoverMutation = useMutation({
    mutationFn: sourcesApi.discover,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      const catalogCount = result.catalog_count || 0
      const reusedCount = result.reused_count || 0
      const totalAdded = result.added_count + reusedCount + catalogCount
      if (totalAdded > 0) {
        const parts: string[] = []
        if (catalogCount > 0) {
          parts.push(`${catalogCount} from curated catalog`)
        }
        if (result.added_count > 0) {
          parts.push(`${result.added_count} new`)
        }
        if (reusedCount > 0) {
          parts.push(`${reusedCount} reused from shared profile`)
        }
        setDiscoveryMessage(`Found ${parts.join(' + ')} source suggestion${totalAdded === 1 ? '' : 's'}`)
      } else {
        setDiscoveryMessage('No new sources found. Try updating your organization profile.')
      }
    },
    onError: () => setDiscoveryMessage('Discovery failed. Check your organization profile and OpenAI config.')
  })

  const acceptMutation = useMutation({
    mutationFn: (sourceId: number) => sourcesApi.accept(sourceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sources'] })
  })

  const dismissMutation = useMutation({
    mutationFn: (sourceId: number) => sourcesApi.dismiss(sourceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sources'] })
  })

  const stopScraperMutation = useMutation({
    mutationFn: (sourceId: number) => sourcesApi.stop(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    }
  })

  const allSources = data || []

  const sources = useMemo(() => {
    // Filter by search query
    let filtered = allSources
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = allSources.filter((source) => {
        const label = STATUS_LABELS[source.scraper_status || ''] || source.scraper_status || ''
        const tagsMatch = source.industry_tags?.some((tag) => tag.toLowerCase().includes(query))
        const relationshipLabel = RELATIONSHIP_LABELS[source.source_relationship || ''] || ''
        return (
          source.name?.toLowerCase().includes(query) ||
          source.source_type?.toLowerCase().includes(query) ||
          source.scraper_status?.toLowerCase().includes(query) ||
          label.toLowerCase().includes(query) ||
          relationshipLabel.toLowerCase().includes(query) ||
          tagsMatch
        )
      })
    }

    // Sort by relationship priority (competitor_platform first, then by name)
    return [...filtered].sort((a, b) => {
      const priorityA = RELATIONSHIP_PRIORITY[a.source_relationship || 'general_directory'] ?? 5
      const priorityB = RELATIONSHIP_PRIORITY[b.source_relationship || 'general_directory'] ?? 5
      if (priorityA !== priorityB) return priorityA - priorityB
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [allSources, searchQuery])

  const getStatusVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status?.toLowerCase()) {
      case 'working':
      case 'ready':
        return 'default'
      case 'suggested':
      case 'analyzing':
        return 'secondary'
      case 'error':
        return 'destructive'
      case 'stopped':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const handleRating = (sourceId: number, rating: number) => {
    updateSourceMutation.mutate({ id: sourceId, quality_rating: rating })
  }

  const handleStart = (source: Source) => {
    // Backend task sets working→ready lifecycle automatically
    runScrapeMutation.mutate(source.name)
  }

  const handleStop = (sourceId: number) => {
    // Call the backend stop endpoint which revokes Celery task and updates both job and source
    stopScraperMutation.mutate(sourceId)
  }

  const StarRating = ({
    rating,
    onRate
  }: {
    rating?: number | null
    onRate: (rating: number) => void
  }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRate(star)
          }}
          className={cn(
            'text-sm transition-colors hover:scale-110',
            rating && star <= rating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-muted-foreground/30 hover:text-yellow-500/50'
          )}
        >
          <Star className={cn('h-3.5 w-3.5', rating && star <= rating && 'fill-current')} />
        </button>
      ))}
    </div>
  )

  const renderSourceActions = (source: Source) => {
    const status = source.scraper_status?.toLowerCase() || ''

    if (status === 'suggested') {
      const isAccepting =
        acceptMutation.isPending && acceptMutation.variables === source.id
      const isDismissing =
        dismissMutation.isPending && dismissMutation.variables === source.id

      return (
        <>
          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              size="sm"
              variant="default"
              onClick={() => acceptMutation.mutate(source.id)}
              disabled={acceptMutation.isPending}
              className="min-w-[88px] transition-opacity duration-200"
            >
              {isAccepting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Following…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Follow
                </>
              )}
            </Button>
          </motion.div>
          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => dismissMutation.mutate(source.id)}
              disabled={dismissMutation.isPending}
              className="transition-opacity duration-200"
            >
              {isDismissing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
            </Button>
          </motion.div>
        </>
      )
    }

    if (status === 'analyzing') {
      return (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Analyzing
        </Badge>
      )
    }

    if (status === 'ready' || status === 'stopped') {
      return (
        <Button
          size="sm"
          onClick={() => handleStart(source)}
          disabled={runningSource === source.name}
        >
          {runningSource === source.name ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Play className="h-4 w-4" />
              Start
            </>
          )}
        </Button>
      )
    }

    if (status === 'working') {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleStop(source.id)}
          disabled={runningSource === source.name}
        >
          {runningSource === source.name ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </Button>
      )
    }

    if (status === 'error') {
      return (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              updateSourceMutation.mutate({ id: source.id, scraper_status: 'analyzing' })
            }}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Retry
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive"
            onClick={() =>
              sourcesApi.delete(source.id).then(() =>
                queryClient.invalidateQueries({ queryKey: ['sources'] })
              )
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )
    }

    return null
  }

  return (
    <div className="space-y-6">
      {/* Page Header - Full Width Above Columns */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Data Sources</h1>
            <p className="mt-1 text-muted-foreground">
              Manage business directories, marketplaces, and company listing sites used to discover prospects. We use these sources to continuously look for prospects for you.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => discoverMutation.mutate()}
              disabled={discoverMutation.isPending}
            >
              {discoverMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Discover Sources
            </Button>
            <AddSourceDialog />
          </div>
        </div>
        {discoveryMessage && (
          <div className="rounded-md bg-muted px-4 py-2 text-sm text-muted-foreground">
            {discoveryMessage}
          </div>
        )}
      </div>

      {/* Sources List */}
      <div>
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Sources
                </h2>
                <span className="text-sm text-muted-foreground">
                  {allSources.length} source{allSources.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search sources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex flex-col gap-3">
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="p-4 rounded-lg border bg-muted/30">
                      <Skeleton className="h-5 w-32 mb-3" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))
                ) : allSources.length === 0 ? (
                  <div className="p-8 text-center">
                    <Database className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-base font-medium text-foreground mb-2">No sources configured</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add a data source or click Discover Sources above.
                    </p>
                  </div>
                ) : sources.length === 0 ? (
                  <div className="p-8 text-center">
                    <Search className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-base font-medium text-foreground mb-2">No sources found</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      No sources match "{searchQuery}"
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                      Clear search
                    </Button>
                  </div>
                ) : (
                  sources.map((source) => {
                    const status = source.scraper_status?.toLowerCase() || ''
                    const companiesCount = source.companies_scraped || source.companies_count || 0
                    const lastRun = source.last_scraped_at || source.last_run

                    return (
                      <div
                        key={source.id}
                        className={cn(
                          'group rounded-lg border transition-colors',
                          status === 'suggested'
                            ? 'border-dashed border-primary/30 bg-primary/5'
                            : 'bg-background hover:bg-muted/30'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3 px-4 py-3">
                          {/* Left: Source info */}
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-foreground truncate">
                                {source.name}
                              </h3>
                              {/* Hide "Ready" badge - all accepted sources can be scraped with GenericAIScraper */}
                              {source.scraper_status && status !== 'ready' && (
                                <Badge
                                  variant={getStatusVariant(source.scraper_status)}
                                  className="text-[11px] px-1.5 py-0 h-5 gap-0.5 shrink-0"
                                >
                                  {status === 'suggested' && <Sparkles className="h-3 w-3" />}
                                  {status === 'working' && <Loader2 className="h-3 w-3 animate-spin" />}
                                  {STATUS_LABELS[source.scraper_status] || source.scraper_status}
                                </Badge>
                              )}
                            </div>

                            {(source.url || source.name) && (
                              <a
                                href={
                                  (source.url || source.name || '').startsWith('http')
                                    ? (source.url || source.name)
                                    : `https://${source.url || source.name}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline truncate w-fit"
                              >
                                <span className="truncate">
                                  {source.url?.replace(/^https?:\/\//, '').replace(/\/$/, '') || source.name}
                                </span>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                              </a>
                            )}

                            {/* Industry Tags and Search Terms Count */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {source.industry_tags && source.industry_tags.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
                                  {source.industry_tags.map((tag) => (
                                    <Badge
                                      key={tag}
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {/* Search terms indicator */}
                              {status !== 'analyzing' && (
                                <Link
                                  to={`/app/sources/${source.id}/config`}
                                  className="flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-primary"
                                >
                                  <Search className="h-3 w-3" />
                                  {source.search_config?.terms?.length || 0} terms
                                </Link>
                              )}
                            </div>

                            {/* Source Profile Enriched Data */}
                            {source.source_profile && (
                              <TooltipProvider>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Curated / registry / access / quality badges */}
                                  {source.source_profile.is_curated && (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal gap-0.5"
                                    >
                                      <Check className="h-2.5 w-2.5" />
                                      Curated
                                    </Badge>
                                  )}

                                  {/* Source relationship badge - shows how this source relates to org */}
                                  {source.source_relationship && source.source_relationship !== 'general_directory' && (
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'text-[10px] px-1.5 py-0 h-4 font-normal',
                                        RELATIONSHIP_STYLES[source.source_relationship] || RELATIONSHIP_STYLES.general_directory
                                      )}
                                    >
                                      {RELATIONSHIP_LABELS[source.source_relationship] || source.source_relationship.replace(/_/g, ' ')}
                                    </Badge>
                                  )}

                                  {source.source_profile.is_registry && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 font-normal gap-0.5"
                                    >
                                      <Shield className="h-2.5 w-2.5" />
                                      Registry
                                      {source.source_profile.registry_country
                                        ? ` ${source.source_profile.registry_country}`
                                        : ''}
                                    </Badge>
                                  )}

                                  {source.source_profile.access_type && (
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'text-[10px] px-1.5 py-0 h-4 font-normal capitalize',
                                        ACCESS_TYPE_STYLES[source.source_profile.access_type] || 'bg-slate-100 text-slate-700 border-slate-200'
                                      )}
                                    >
                                      {source.source_profile.access_type.replace(/_/g, ' ')}
                                    </Badge>
                                  )}

                                  {source.source_profile.contact_data_quality && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-0.5">
                                          {Array.from({ length: 4 }).map((_, idx) => (
                                            <span
                                              key={idx}
                                              className={cn(
                                                'h-1.5 w-1.5 rounded-full',
                                                idx < (QUALITY_SCORE[source.source_profile?.contact_data_quality || 'unknown'] || 0)
                                                  ? 'bg-primary'
                                                  : 'bg-muted-foreground/25'
                                              )}
                                            />
                                          ))}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          Contact quality: {source.source_profile.contact_data_quality}
                                        </p>
                                        {source.source_profile.data_freshness && (
                                          <p className="text-xs text-muted-foreground">
                                            Freshness: {source.source_profile.data_freshness}
                                          </p>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}

                                  {/* Country coverage badges */}
                                  {source.source_profile.covered_countries.length > 0 && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1">
                                          <Globe className="h-3 w-3 text-muted-foreground" />
                                          <span className="text-[10px] text-muted-foreground">
                                            {source.source_profile.covered_countries.length <= 3
                                              ? source.source_profile.covered_countries.join(', ')
                                              : `${source.source_profile.covered_countries.slice(0, 2).join(', ')} +${source.source_profile.covered_countries.length - 2}`}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="font-medium mb-1">Countries covered:</p>
                                        <p className="text-xs">{source.source_profile.covered_countries.join(', ')}</p>
                                        {source.source_profile.country_distribution && Object.keys(source.source_profile.country_distribution).length > 0 && (
                                          <>
                                            <p className="font-medium mt-2 mb-1">Verified distribution:</p>
                                            <div className="text-xs space-y-0.5">
                                              {Object.entries(source.source_profile.country_distribution)
                                                .sort(([, a], [, b]) => b - a)
                                                .slice(0, 5)
                                                .map(([country, count]) => (
                                                  <div key={country}>{country}: {count.toLocaleString()}</div>
                                                ))}
                                            </div>
                                          </>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}


                                  {/* Estimated listings */}
                                  {source.source_profile.estimated_listings && (
                                    <span className="text-[10px] text-muted-foreground">
                                      ~{source.source_profile.estimated_listings} listings
                                    </span>
                                  )}

                                  {/* Times discovered (shared source indicator) */}
                                  {source.source_profile.times_discovered > 1 && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px] px-1.5 py-0 h-4 font-normal gap-0.5"
                                        >
                                          <Users className="h-2.5 w-2.5" />
                                          {source.source_profile.times_discovered}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Used by {source.source_profile.times_discovered} organizations</p>
                                        {source.source_profile.times_scraped > 0 && (
                                          <p className="text-xs text-muted-foreground">
                                            Scraped {source.source_profile.times_scraped} times total
                                          </p>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}

                                  {/* Average quality rating from community */}
                                  {source.source_profile.avg_quality_rating && source.source_profile.avg_quality_rating > 0 && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-0.5">
                                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                          <span className="text-[10px] text-muted-foreground">
                                            {source.source_profile.avg_quality_rating.toFixed(1)}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Community average rating</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </TooltipProvider>
                            )}

                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <StarRating
                                rating={source.quality_rating || null}
                                onRate={(rating) => handleRating(source.id, rating)}
                              />
                              {companiesCount > 0 && (
                                <Link
                                  to="/companies"
                                  className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary hover:underline"
                                >
                                  {companiesCount.toLocaleString()} companies
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                              )}
                              {lastRun && (
                                <span>
                                  Last run{' '}
                                  <span className="text-foreground">
                                    {new Date(lastRun).toLocaleDateString()}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Right: Actions */}
                          <div className="flex gap-2 items-center shrink-0 pt-0.5">
                            {/* Settings button for non-suggested sources */}
                            {status !== 'suggested' && status !== 'analyzing' && (
                              <Button
                                asChild
                                size="sm"
                                variant="ghost"
                                title="Configure search terms"
                              >
                                <Link to={`/app/sources/${source.id}/config`}>
                                  <Settings2 className="h-4 w-4" />
                                </Link>
                              </Button>
                            )}
                            {renderSourceActions(source)}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </CardContent>
          </Card>
      </div>

    </div>
  )
}
